/**
 * Engagements.jsx — Iter 49, reworked in Iter 66. Founder-side view of every
 * marketplace engagement (Services / Credit / Marketing).
 *
 * Each tile is now expandable: the collapsed row carries a progress bar and the
 * headline status, and opening it reveals the full step timeline, the scope and
 * money, the document state, and — on guided service requests past the
 * agreement — the thread with the partner.
 *
 * Progress comes off the server (`progress.pct`, `steps`) rather than being
 * re-derived here, so the tile, the marketplace banner and the wizard timeline
 * can never disagree about how far along something is.
 */
import './engagements-motion.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/api';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Buildings, Bank, Megaphone, Handshake, Lock, Check, Clock, ArrowRight,
  ClockCounterClockwise, Calendar, CaretDown, ChatCircleDots, Receipt, FileText, X,
  DownloadSimple, Warning, Spinner,
} from '@phosphor-icons/react';
import { useCurrency } from '@/hooks/useCurrency';
import { formatDate } from '@/utils/datetime';
import ServiceChat, { ServiceChatLocked } from '@/components/ServiceChat';
import ProgressDonut from '@/components/ProgressDonut';

// The `success` Tailwind token, as a literal — it's a theme colour, not a CSS
// variable, so an inline style has to name the value.
const SUCCESS = '#22c55e';

const TYPE_CFG = {
  service: { accent: '#10b981', Icon: Buildings, label: 'Services' },
  credit:  { accent: '#f59e0b', Icon: Bank,      label: 'Credit'   },
  agency:  { accent: '#6366f1', Icon: Megaphone, label: 'Marketing'},
};

const STATUS_LABEL = {
  pending_payment: 'Pending payment',
  active:          'Active',
  delivered:       'Ready for review',
  closed:          'Closed',
  // (Iter 65) Extra stages that only the guided service flow reaches.
  draft:             'Not started',
  agreement_signed:  'Awaiting your documents',
  awaiting_quote:    'With partner for quote',
  quote_ready:       'Quote ready to approve',
  // (Iter 68) The post-payment review loop.
  revisions:         'Changes requested',
  awaiting_balance:  'Balance due',
};

// Statuses where the founder is the one holding things up — drives the amber
// treatment on the tile and the "needs you" count in the header.
const NEEDS_FOUNDER = new Set(['draft', 'agreement_signed', 'quote_ready', 'pending_payment', 'delivered', 'awaiting_balance']);
const FINISHED = new Set(['delivered', 'closed']);

// The guided flow owns its own screens, so those cards deep-link into the wizard
// rather than trying to reproduce each step inline here.
const SERVICE_FLOW = 'service_v2';
const IN_WIZARD = new Set(['draft', 'agreement_signed', 'awaiting_quote', 'quote_ready', 'pending_payment', 'delivered', 'revisions', 'awaiting_balance']);
const WIZARD_CTA = {
  draft:             'Accept agreement',
  agreement_signed:  'Upload documents',
  awaiting_quote:    'View progress',
  quote_ready:       'Review quote',
  pending_payment:   'Complete payment',
  delivered:         'Review delivery',
  revisions:         'Changes requested',
  awaiting_balance:  'Settle the balance',
};

const Engagements = ({ category }) => {
  const [engs, setEngs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState('');
  const [cancelling, setCancelling] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/marketplace-engagements/mine');
      const all = r.data.engagements || [];
      // Filter to the category that owns this tab (`service` / `credit` / `agency`).
      // If no category supplied, show everything (kept for legacy / future contexts).
      setEngs(category ? all.filter((e) => e.vendor_type === category) : all);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load engagements'); }
    finally { setLoading(false); }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const payCharge = async (charge) => {
    setPaying(charge.id);
    try {
      await api.post(`/billing/charges/${charge.id}/mark-paid`, {});
      toast.success('Payment recorded — engagement unlocked.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment failed'); }
    finally { setPaying(''); }
  };

  // Founder-only, and only before any money is involved — the server enforces
  // the same pre-payment status check. Type-to-confirm rather than a plain OK/
  // Cancel dialog, since this is destructive and easy to fat-finger.
  const cancelRequest = async (e) => {
    const typed = window.prompt(
      `This will permanently cancel your ${e.offering?.catalog_item_name || 'service'} request with ${e.vendor_name}. This can't be undone.\n\nType "cancel service" to confirm.`
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== 'cancel service') {
      toast.error('Cancellation not confirmed — text didn\'t match "cancel service".');
      return;
    }
    setCancelling(e.id);
    try {
      await api.delete(`/service-requests/${e.id}`);
      toast.success('Request cancelled.');
      if (expanded === e.id) setExpanded(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not cancel this request'); }
    finally { setCancelling(''); }
  };

  // Opening a thread is the read receipt server-side; mirror it locally so the
  // badge clears without a full reload of every tile. Returns the same array
  // when there's nothing to clear — the chat calls this on every poll tick, and
  // a fresh array each time would re-render the whole list for no reason.
  const clearUnread = useCallback((id) => {
    setEngs((prev) => (prev.some((e) => e.id === id && (e.chat?.unread || 0) > 0)
      ? prev.map((e) => (e.id === id ? { ...e, chat: { ...e.chat, unread: 0 } } : e))
      : prev));
  }, []);

  const counts = useMemo(() => ({
    needsYou: engs.filter((e) => NEEDS_FOUNDER.has(e.status)).length,
    unread: engs.reduce((s, e) => s + (e.chat?.unread || 0), 0),
  }), [engs]);

  const selected = engs.find((e) => e.id === expanded) || null;

  if (loading) return <div className="p-6 text-sm text-text-muted">Loading engagements…</div>;

  return (
    <div className="p-4 sm:p-6" data-testid="engagements-page">
      {/* Full width rather than a centred column — the conversation rail claims
          the right, and the tiles spread into what's left. */}
      <div className="max-w-[1600px] mx-auto">
        <div className="engagement-summary">
          {(counts.needsYou > 0 || counts.unread > 0) && (
            <div className="flex items-center gap-3 mt-2.5 flex-wrap text-[11px]" data-testid="engagements-summary">
              {counts.needsYou > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-warning/15 text-warning font-semibold">
                  <Clock size={10} weight="bold" /> {counts.needsYou} waiting on you
                </span>
              )}
              {counts.unread > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-info/15 text-info font-semibold">
                  <ChatCircleDots size={10} weight="fill" /> {counts.unread} unread message{counts.unread === 1 ? '' : 's'}
                </span>
              )}
            </div>
          )}
        </div>

        {engs.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-text-muted bg-surface">
            <Handshake size={26} className="mx-auto mb-2 text-text-muted" />
            {category === 'service'
              ? <>No service requests yet. Browse the marketplace, pick a product and choose a partner &mdash; your managed request shows up here.</>
              : <>No engagements yet. Browse a marketplace, hit <strong className="text-text-primary">Request intro</strong> on any vendor, and they&rsquo;ll show up here.</>}
          </div>
        ) : (
          // 65 / 35 split. flex-grow ratios rather than percentage widths so the
          // gap is absorbed instead of pushing the total past 100%.
          <div className="flex flex-col xl:flex-row gap-4 items-start">
            <div className="w-full min-w-0 space-y-3 xl:flex-[65_1_0%]">
              {engs.map((e) => (
                <EngagementTile
                  key={e.id}
                  e={e}
                  open={expanded === e.id}
                  dimmed={!!expanded && expanded !== e.id}
                  onToggle={() => setExpanded(expanded === e.id ? null : e.id)}
                  onPay={payCharge}
                  paying={paying}
                  onCancel={cancelRequest}
                  cancelling={cancelling}
                />
              ))}
            </div>
            <ChatRail
              e={selected}
              onClose={() => setExpanded(null)}
              onRead={() => selected && clearUnread(selected.id)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------- chat rail ----- */

/**
 * The docked conversation for whichever tile is open.
 *
 * Keyed on the engagement id so switching tiles unmounts the old thread and
 * mounts a fresh one — messages, the unsent draft, the poll timer and the scroll
 * position all belong to one engagement and must not carry across. Without the
 * key React would reuse the instance and briefly show the previous partner's
 * messages under the new partner's name.
 */
const ChatRail = ({ e, onClose, onRead }) => {
  const cfg = e ? (TYPE_CFG[e.vendor_type] || TYPE_CFG.service) : TYPE_CFG.service;
  const isGuided = e?.flow === SERVICE_FLOW;
  return (
    <aside className="w-full min-w-0 xl:flex-[35_1_0%] xl:sticky xl:top-6" data-testid="engagements-chat-rail">
      <div className="bg-surface border border-border rounded-xl p-4">
        {!e ? (
          <div className="text-center py-8" data-testid="engagements-chat-rail-empty">
            <ChatCircleDots size={24} className="mx-auto mb-2 text-text-muted" />
            <div className="text-sm font-semibold text-text-primary">No engagement selected</div>
            <p className="text-[11px] text-text-muted mt-1 leading-relaxed">
              Open an engagement on the left to see its detail and message that partner.
              Every engagement keeps its own separate thread.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Conversation</div>
                <div className="text-sm font-heading font-bold text-text-primary truncate">{e.vendor_name}</div>
                {e.offering?.catalog_item_name && (
                  <div className="text-[10px] text-text-muted truncate">{e.offering.catalog_item_name}</div>
                )}
              </div>
              <button onClick={onClose} data-testid="engagements-chat-rail-close"
                className="text-text-muted hover:text-text-primary flex-shrink-0" aria-label="Close conversation">
                <X size={14} weight="bold" />
              </button>
            </div>
            {!isGuided ? (
              <div className="text-[11px] text-text-muted leading-relaxed py-4" data-testid="engagements-chat-rail-unsupported">
                Messaging is available on managed service requests. For this engagement, reach
                {' '}{e.vendor_name} on the email address on the tile.
              </div>
            ) : e.chat?.open ? (
              <ServiceChat key={e.id} requestId={e.id} accent={cfg.accent}
                counterpartyFallback={e.vendor_name} onRead={onRead} compact />
            ) : (
              <ServiceChatLocked accent={cfg.accent} testid={`engagement-chat-locked-${e.id}`} />
            )}
          </>
        )}
      </div>
    </aside>
  );
};

/* ------------------------------------------------------------------ tile ----- */

// Pre-payment statuses only — matches the server's own guard on DELETE
// /service-requests/{id} (abandon_service_request), which 400s past this point.
const CANCELLABLE = new Set(['draft', 'agreement_signed', 'awaiting_quote', 'quote_ready']);

const EngagementTile = ({ e, open, onToggle, onPay, paying, onCancel, cancelling, dimmed }) => {
  const { format } = useCurrency();
  const cfg = TYPE_CFG[e.vendor_type] || TYPE_CFG.service;
  const Icon = cfg.Icon;
  const isGuided = e.flow === SERVICE_FLOW;
  // Guided requests are settled inside the wizard (which also shows the quote
  // and issues the invoice), so don't offer an inline pay button.
  const isLocked = e.is_locked && !isGuided;
  const inWizard = isGuided && IN_WIZARD.has(e.status);
  const pendingCharge = (e.charges || []).find((c) => c.status === 'pending');
  const quoted = e.quote?.total;
  const pct = e.progress?.pct ?? 0;
  const unread = e.chat?.unread || 0;
  const needsYou = NEEDS_FOUNDER.has(e.status);
  // (Iter 68) For guided requests `delivered` means "awaiting your review", not
  // done — only `closed` is terminal. Legacy rows keep their old milestones.
  const finished = isGuided ? e.status === 'closed' : FINISHED.has(e.status);

  // Selection and hover are drawn in the category accent rather than the grey
  // theme tokens. The accent is a fixed hex, so it reads identically in light and
  // dark — `border-text-primary/50` and `ring-text-primary/20` were near-white and
  // vanished on a light background, and `hover:bg-background/40` has no light-theme
  // override (only the non-hover variant does), so it fell through to the dark
  // base rule and washed the row grey.
  const borderClass = open ? ''                       // colour comes from selectedStyle
    : isLocked ? 'border-warning/40'
    : needsYou ? 'border-warning/30'
    : 'border-border';
  const selectedStyle = open
    ? { borderColor: cfg.accent, boxShadow: `0 0 0 1px ${cfg.accent}55, 0 10px 30px -14px ${cfg.accent}80` }
    : undefined;

  return (
    // Dimmed = another tile owns the conversation on the right. Blurred and faded
    // rather than hidden, and still clickable, so the list stays navigable while
    // making it unambiguous which thread is open.
    <div
      className={`engagement-tile bg-surface border rounded-xl overflow-hidden transition-all duration-200 ${borderClass} ${
        dimmed ? 'engagement-unselected' : ''
      } ${isLocked && !dimmed ? 'opacity-90' : ''}`}
      style={{ ...selectedStyle, '--engagement-accent': cfg.accent }}
      data-testid={`engagement-card-${e.id}`}
      data-dimmed={dimmed ? 'true' : 'false'}>
      {/* Collapsed header — the whole row is the toggle. Tint comes from CSS vars
          so both states stay class-driven; an inline background would outrank the
          :hover rule and kill the hover entirely. A plain div (not a <button>) so
          the cancel action below can nest a real <button> next to the progress
          ring without landing inside another button. */}
      <div role="button" tabIndex={0} onClick={onToggle}
        onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onToggle(); } }}
        aria-expanded={open}
        aria-controls={`engagement-detail-panel-${e.id}`}
        data-testid={`engagement-toggle-${e.id}`}
        style={{ '--tile-hover': `${cfg.accent}14`, '--tile-open': open ? `${cfg.accent}0d` : 'transparent' }}
        className="engagement-tile-header w-full text-left p-4 transition-colors bg-[var(--tile-open)] hover:bg-[var(--tile-hover)] cursor-pointer">
        <div className="flex items-start gap-3 flex-wrap">
          <span className="engagement-icon w-9 h-9 rounded-lg inline-flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${cfg.accent}1a`, color: cfg.accent }}>
            <Icon size={16} weight="fill" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-[17px] font-heading font-bold text-text-primary truncate">{e.vendor_name}</h3>
              <span className="engagement-type text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                style={{ backgroundColor: `${cfg.accent}1a`, color: cfg.accent }}>{cfg.label}</span>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${
                isLocked || needsYou ? 'bg-warning/15 text-warning' : 'bg-success/15 text-success'}`}>
                {STATUS_LABEL[e.status] || e.status}
              </span>
              {unread > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-info/15 text-info inline-flex items-center gap-1"
                  data-testid={`engagement-unread-${e.id}`}>
                  <ChatCircleDots size={10} weight="fill" /> {unread}
                </span>
              )}
            </div>
            {e.offering?.catalog_item_name && (
              <div className="text-[14px] text-text-secondary mt-1">{e.offering.catalog_item_name}</div>
            )}
            <div className="flex items-center gap-3 text-[11.5px] text-text-muted mt-1.5 flex-wrap">
              {quoted != null ? (
                <span className="inline-flex items-center gap-1">{format(quoted, e.quote.currency || 'INR')} quoted</span>
              ) : e.offering?.gross_price > 0 && (
                <span className="inline-flex items-center gap-1">{format((e.offering.gross_price || 0) + (e.offering.govt_fees || 0), e.offering.currency || 'INR')} {e.offering.cadence === 'one_time' ? 'one-time' : `/ ${e.offering.cadence.replace('_', ' ')}`}</span>
              )}
              {e.offering?.delivery_days && (
                <span className="inline-flex items-center gap-1"><Clock size={10} weight="bold" /> Delivery in {e.offering.delivery_days} day{e.offering.delivery_days === 1 ? '' : 's'}</span>
              )}
              <span className="inline-flex items-center gap-1"><ClockCounterClockwise size={10} weight="bold" /> {new Date(e.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
            </div>
          </div>
          {/* Progress ring — always visible, collapsed or not. */}
          <div className="engagement-progress flex items-center gap-2.5 flex-shrink-0">
            <div className="engagement-step-label text-right hidden sm:block">
              <div className="text-[12px] font-semibold text-text-primary leading-tight">
                {e.progress?.total_steps
                  ? <>Step {Math.min((e.progress.done_steps || 0) + (finished ? 0 : 1), e.progress.total_steps)} of {e.progress.total_steps}</>
                  : (STATUS_LABEL[e.status] || e.status)}
              </div>
              <div className="text-[11px] text-text-muted leading-tight">
                {e.progress?.current_label || (finished ? 'Complete' : '')}
              </div>
              {e.progress?.current_eta && (
                <div className="text-[10.5px] text-text-muted leading-tight">by {formatDate(e.progress.current_eta)}</div>
              )}
            </div>
            <div className="engagement-ring flex flex-col items-center gap-1">
              <ProgressDonut pct={pct} size={56} stroke={5.5}
                color={finished ? SUCCESS : cfg.accent}
                testid={`engagement-progress-${e.id}`} />

            </div>
            <CaretDown size={14} weight="bold" className="text-text-muted transition-transform"
              style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
          </div>
        </div>
        {/* Narrow screens lose the ring's side label, so restate it underneath. */}
        <div className="text-[11.5px] text-text-muted mt-2 sm:hidden">
          {e.progress?.total_steps
            ? <>Step {Math.min((e.progress.done_steps || 0) + (finished ? 0 : 1), e.progress.total_steps)} of {e.progress.total_steps}{e.progress.current_label ? ` · ${e.progress.current_label}` : ''}</>
            : (e.progress?.current_label || STATUS_LABEL[e.status] || e.status)}
        </div>
      </div>

      <div className="engagement-disclosure" data-open={open ? 'true' : 'false'} aria-hidden={!open} inert={!open ? true : undefined} id={`engagement-detail-panel-${e.id}`}>
        <div className="engagement-disclosure-inner">
        <div className="border-t border-border p-4 space-y-4" data-testid={`engagement-detail-${e.id}`}>
          <ScopeFacts e={e} accent={cfg.accent} />

          {isGuided && (e.steps || []).length > 0 && <MiniTimeline steps={e.steps} accent={cfg.accent} />}

          {inWizard && (
            <Link to={`/startup/services/request/${e.id}`}
              data-testid={`engagement-continue-${e.id}`}
              className={`p-3 rounded-lg border flex items-center gap-3 group transition-colors ${
                needsYou
                  ? 'border-warning/50 bg-warning/10 hover:bg-warning/15'
                  : 'border-border bg-background hover:border-text-primary'}`}>
              {needsYou ? (
                <Warning size={16} weight="fill" className="text-warning flex-shrink-0" />
              ) : (
                <Icon size={16} weight="fill" style={{ color: cfg.accent }} className="flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold ${needsYou ? 'text-warning' : 'text-text-primary'}`}>
                  {WIZARD_CTA[e.status]}{needsYou ? ' — action needed' : ''}
                </div>
                <div className="text-[11px] text-text-muted">
                  {e.status === 'quote_ready' ? `${e.vendor_name} sent a final quote — approve it to start.`
                    : e.status === 'pending_payment' ? 'Settle the approved quote to release the work.'
                    : e.status === 'awaiting_quote' ? 'Your partner is reviewing the documents you sent.'
                    : e.status === 'delivered' ? 'Your partner delivered the final document — preview it, then approve or request changes.'
                    : e.status === 'revisions' ? 'You asked for changes; your partner will re-deliver.'
                    : e.status === 'awaiting_balance' ? 'Approved — settle the balance to release your document into your vault.'
                    : e.status === 'draft' ? 'Accept the service agreement to move this request forward.'
                    : e.status === 'agreement_signed' ? 'Upload the documents your partner needs to quote.'
                    : 'Pick up your managed service request where you left off.'}
                </div>
              </div>
              <ArrowRight size={14} weight="bold" className={`flex-shrink-0 ${needsYou ? 'text-warning' : 'text-text-muted group-hover:text-text-primary'}`} />
            </Link>
          )}

          {isLocked && pendingCharge && (
            <div className="p-3 rounded-lg bg-warning/10 border border-warning/30 flex items-center gap-3 flex-wrap" data-testid={`engagement-locked-${e.id}`}>
              <Lock size={14} weight="fill" className="text-warning flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-text-primary">Payment required to unlock</div>
                <div className="text-[11px] text-text-muted">{pendingCharge.label} &middot; <strong className="text-text-primary">{format(pendingCharge.amount, pendingCharge.currency || 'INR')}</strong></div>
              </div>
              <button onClick={() => onPay(pendingCharge)} disabled={paying === pendingCharge.id}
                data-testid={`engagement-pay-${e.id}`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white" style={{ backgroundColor: cfg.accent }}>
                {paying === pendingCharge.id ? 'Processing…' : 'Pay now'} {paying !== pendingCharge.id && <ArrowRight size={10} weight="bold" />}
              </button>
            </div>
          )}

          {!isLocked && e.vendor_type === 'agency' && e.workspace_engagement_id && (
            <Link to={`/startup/marketing/engagements/${e.workspace_engagement_id}`}
              data-testid={`engagement-open-workspace-${e.id}`}
              className="p-3 rounded-lg border border-border bg-background hover:border-text-primary flex items-center gap-3 group">
              <Calendar size={16} weight="fill" style={{ color: cfg.accent }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-text-primary">Open content calendar &amp; workspace</div>
                <div className="text-[11px] text-text-muted">Post requests, track delivery, comment on posts &mdash; all in one place.</div>
              </div>
              <ArrowRight size={14} weight="bold" className="text-text-muted group-hover:text-text-primary flex-shrink-0" />
            </Link>
          )}

          <div className="flex items-center gap-3 flex-wrap text-[11px] text-text-muted">
            {e.vendor_contact_email && !isLocked && (
              <a href={`mailto:${e.vendor_contact_email}`}
                className="truncate inline-flex items-center gap-1 underline decoration-dotted underline-offset-2 hover:decoration-solid"
                style={{ color: cfg.accent }}>
                {e.vendor_contact_email}
              </a>
            )}
            {/* (Iter 67) For guided requests the gate is payment, not submit —
                so say which of the two states this engagement is actually in. */}
            {!isLocked && e.offering?.required_documents?.length > 0 && (!isGuided || e.paid_at) && (
              <span className="inline-flex items-center gap-1.5">
                <Check size={11} weight="bold" className="text-success" />
                Partner can access your {e.offering.required_documents.length} required document{e.offering.required_documents.length === 1 ? '' : 's'} from your vault.
              </span>
            )}
            {isGuided && !e.paid_at && (
              <span className="inline-flex items-center gap-1.5">
                <Lock size={11} weight="fill" />
                Your documents, contact and ID details stay sealed from {e.vendor_name} until payment clears.
              </span>
            )}
          </div>
          <div className="flex justify-end">

              {isGuided && CANCELLABLE.has(e.status) && (
                <button onClick={(ev) => { ev.stopPropagation(); onCancel(e); }}
                  disabled={cancelling === e.id}
                  data-testid={`engagement-cancel-${e.id}`}
                  className="text-[12px] font-medium rounded-full border border-danger/25 px-3 py-1.5 inline-flex items-center gap-1 text-danger hover:bg-danger/10 transition-colors disabled:opacity-50">
                  {cancelling === e.id ? <Spinner size={10} weight="bold" className="animate-spin" /> : <X size={10} weight="bold" />}
                  {cancelling === e.id ? 'Cancelling…' : 'Cancel engagement'}
                </button>
              )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- detail ----- */

/** Scope, money and dates — the "what am I actually buying" block. */
const ScopeFacts = ({ e, accent }) => {
  const { format } = useCurrency();
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const offering = e.offering || {};
  const quote = e.quote || {};
  const billing = e.billing || {};
  const currency = quote.currency || offering.currency || 'INR';
  const facts = [];

  const downloadInvoice = useCallback(async () => {
    setDownloadingInvoice(true);
    try {
      const r = await api.get(`/service-requests/${e.id}/invoice`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${e.invoice_number || 'invoice'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invoice not available yet');
    } finally {
      setDownloadingInvoice(false);
    }
  }, [e.id, e.invoice_number]);

  if (offering.catalog_item_name) facts.push({ label: 'Service', value: offering.catalog_item_name });
  if (quote.total != null) {
    facts.push({ label: 'Quoted total', value: format(quote.total, currency), accent: true, Icon: Receipt });
    if ((quote.gst_amount || 0) > 0) {
      facts.push({ label: `Of which GST (${quote.gst_pct || 18}%)`, value: format(quote.gst_amount, currency) });
    }
    if ((quote.govt_fees || 0) > 0) {
      facts.push({ label: 'Of which govt fees', value: format(quote.govt_fees, currency) });
    }
  } else if (offering.gross_price > 0) {
    facts.push({
      label: 'Listed price',
      value: format((offering.gross_price || 0) + (offering.govt_fees || 0), currency),
      Icon: Receipt,
    });
  }
  if ((billing.balance_due || 0) > 0) {
    facts.push({ label: 'Balance on delivery', value: format(billing.balance_due, currency) });
  }
  if (e.invoice_number) {
    facts.push({
      label: 'Invoice',
      value: downloadingInvoice ? 'Preparing…' : e.invoice_number,
      Icon: downloadingInvoice ? FileText : DownloadSimple,
      onClick: downloadInvoice,
      disabled: downloadingInvoice,
    });
  }
  if (e.agreement_accepted_at) facts.push({ label: 'Agreement signed', value: formatDate(e.agreement_accepted_at) });
  if (e.docs_submitted_at) facts.push({ label: 'Documents sent', value: formatDate(e.docs_submitted_at) });
  if (e.paid_at) facts.push({ label: 'Paid', value: formatDate(e.paid_at) });

  if (facts.length === 0) return null;

  return (
    <div className="engagement-facts rounded-xl border border-border bg-background p-3.5" data-testid={`engagement-facts-${e.id}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-2">Engagement detail</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2.5">
        {facts.map((f) => (
          <div key={f.label}>
            <div className="text-[9px] uppercase tracking-wider font-bold text-text-muted">{f.label}</div>
            {f.onClick ? (
              <button
                type="button"
                onClick={f.onClick}
                disabled={f.disabled}
                data-testid={`engagement-invoice-download-${e.id}`}
                className="text-[12px] font-semibold inline-flex items-center gap-1 underline decoration-dotted underline-offset-2 hover:decoration-solid disabled:opacity-50 disabled:no-underline"
                style={{ color: accent }}
              >
                {f.Icon && <f.Icon size={10} weight="bold" />} {f.value}
              </button>
            ) : (
              <div className={`text-[12px] font-semibold inline-flex items-center gap-1 ${f.accent ? '' : 'text-text-primary'}`}
                style={f.accent ? { color: accent } : {}}>
                {f.Icon && <f.Icon size={10} weight="bold" />} {f.value}
              </div>
            )}
          </div>
        ))}
      </div>
      {quote.notes && (
        <div className="mt-2.5 pt-2.5 border-t border-border">
          <div className="text-[9px] uppercase tracking-wider font-bold text-text-muted mb-0.5">Note from your partner</div>
          <p className="text-[12px] text-text-secondary leading-relaxed">{quote.notes}</p>
        </div>
      )}
    </div>
  );
};

/**
 * Compact horizontal read of the server's step list. The wizard has the full
 * vertical timeline with blurbs — inside a tile there's only room for the shape.
 */
const MiniTimeline = ({ steps, accent }) => (
  <div className="engagement-timeline rounded-xl border border-border bg-background p-3.5" data-testid="engagement-mini-timeline">
    <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-2.5">Progress</div>
    <div className="engagement-timeline-steps flex items-start gap-0">
      {steps.map((s, i) => {
        const done = s.state === 'done';
        const current = s.state === 'current';
        return (
          <React.Fragment key={s.key}>
            {i > 0 && (
              <span className="h-px flex-1 mt-2.5 min-w-[8px]"
                style={{ backgroundColor: done || current ? accent : 'var(--border)' }} />
            )}
            <div className="flex flex-col items-center gap-1 flex-shrink-0" style={{ maxWidth: 78 }}
              data-testid={`engagement-step-${s.key}`}>
              {done ? (
                <span className="w-5 h-5 rounded-full inline-flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: accent, color: '#fff' }}><Check size={9} weight="bold" /></span>
              ) : current ? (
                <span className="w-5 h-5 rounded-full inline-flex items-center justify-center border-2 flex-shrink-0"
                  style={{ borderColor: accent }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accent }} />
                </span>
              ) : (
                <span className="w-5 h-5 rounded-full border border-dashed border-border flex-shrink-0" />
              )}
              <span className={`text-[9px] text-center leading-tight ${done || current ? 'text-text-secondary' : 'text-text-muted'}`}>
                {s.label}
              </span>
              {s.at && <span className="text-[8px] text-text-muted">{formatDate(s.at)}</span>}
              {!s.at && s.eta && <span className="text-[8px]" style={{ color: accent }}>ETA {formatDate(s.eta)}</span>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

export default Engagements;
